import {
  Activity,
  ArrowLeft,
  ArrowRight,
  BadgeDollarSign,
  BookOpen,
  BriefcaseBusiness,
  Check,
  ChevronRight,
  Clock3,
  Goal,
  Search,
  Sparkles,
  Target,
  X
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode
} from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { api } from "../../lib/api";
import { eventLabels, formatDate } from "../../lib/format";
import type { CreateKind } from "../../types";
import { Button } from "../ui";
import { QuickCaptureModal } from "./QuickCaptureModal";

const draftKey = "earth-online:v3:quick-draft";

export function GlobalModals() {
  const { modal, modalDefaults, closeModal } = useApp();

  useEffect(() => {
    if (!modal) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeModal, modal]);

  if (!modal) return null;
  if (modal === "quick") return <QuickCaptureModal />;
  if (modal === "search") return <SearchModal />;
  return <EntityModal kind={modal} taskDefaults={modalDefaults} />;
}

function ModalFrame({
  children,
  title,
  subtitle,
  wide = false,
  onClose
}: {
  children: ReactNode;
  title: string;
  subtitle?: string;
  wide?: boolean;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className={`modal ${wide ? "modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal__header">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="modal__close" onClick={onClose} aria-label="关闭">
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function QuickRecordModal() {
  const { data, closeModal, mutate } = useApp();
  const [step, setStep] = useState<"write" | "review">("write");
  const [text, setText] = useState(() => window.localStorage.getItem(draftKey) ?? "");
  const [title, setTitle] = useState("");
  const [eventType, setEventType] = useState("PROGRESS");
  const [result, setResult] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [projectId, setProjectId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [skillId, setSkillId] = useState("");
  const [contactId, setContactId] = useState("");

  useEffect(() => {
    if (text) window.localStorage.setItem(draftKey, text);
    else window.localStorage.removeItem(draftKey);
  }, [text]);

  const prepareReview = () => {
    const first = text
      .split(/[。！？\n]/)
      .map((part) => part.trim())
      .find(Boolean);
    setTitle(first?.slice(0, 80) || "新推进记录");
    setStep("review");
  };

  const submit = async () => {
    const links = [
      projectId && { entityType: "project", entityId: projectId },
      taskId && { entityType: "task", entityId: taskId },
      skillId && { entityType: "skill", entityId: skillId }
      ,
      contactId && { entityType: "contact", entityId: contactId }
    ].filter(Boolean);
    const saved = await mutate(
      () =>
        api.create("events", {
          title,
          eventType,
          factualContent: text,
          resultSummary: result,
          nextAction,
          occurredAt: new Date().toISOString(),
          links,
          aiAssisted: false
        }),
      "记录已写入时间轴"
    );
    if (saved) {
      window.localStorage.removeItem(draftKey);
      closeModal();
    }
  };

  return (
    <ModalFrame
      title={step === "write" ? "记录正在发生的事" : "确认这条事实记录"}
      subtitle={step === "write" ? "先写原话，系统不会在你确认前保存。" : "你始终拥有最终决定权。"}
      wide
      onClose={closeModal}
    >
      <div className="quick-steps">
        <span className={step === "write" ? "active" : "done"}><i>1</i> 原始记录</span>
        <b />
        <span className={step === "review" ? "active" : ""}><i>2</i> 结构确认</span>
      </div>
      {step === "write" ? (
        <>
          <div className="quick-compose">
            <textarea
              autoFocus
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="例如：今天完成了报价结构的第一版，发现成本核算还缺包装和运输两项，下一步明早补齐。"
              maxLength={2000}
            />
            <div className="quick-compose__meta">
              <span><Clock3 size={14} /> 草稿已自动保存在此设备</span>
              <span>{text.length} / 2000</span>
            </div>
          </div>
          <div className="modal__footer">
            <Button variant="ghost" onClick={closeModal}>稍后再写</Button>
            <Button onClick={prepareReview} disabled={!text.trim()} icon={<ArrowRight size={16} />}>
              整理并确认
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="form-grid form-grid--two modal__body">
            <label className="field field--full">
              <span>事件标题</span>
              <input value={title} onChange={(event) => setTitle(event.target.value)} autoFocus />
            </label>
            <label className="field">
              <span>事件类型</span>
              <select value={eventType} onChange={(event) => setEventType(event.target.value)}>
                {Object.entries(eventLabels).map(([value, label]) => (
                  <option value={value} key={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>关联项目</span>
              <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
                <option value="">不关联</option>
                {data?.projects.map((project) => (
                  <option key={project.id} value={project.id}>{project.title}</option>
                ))}
              </select>
            </label>
            <label className="field field--full">
              <span>事实原文</span>
              <textarea value={text} onChange={(event) => setText(event.target.value)} rows={4} />
            </label>
            <label className="field">
              <span>结果或影响</span>
              <textarea
                value={result}
                onChange={(event) => setResult(event.target.value)}
                placeholder="这件事带来了什么变化？"
                rows={3}
              />
            </label>
            <label className="field">
              <span>下一步</span>
              <textarea
                value={nextAction}
                onChange={(event) => setNextAction(event.target.value)}
                placeholder="最自然的下一步是什么？"
                rows={3}
              />
            </label>
            <label className="field">
              <span>关联任务</span>
              <select value={taskId} onChange={(event) => setTaskId(event.target.value)}>
                <option value="">不关联</option>
                {data?.tasks
                  .filter((task) => task.status !== "COMPLETED")
                  .map((task) => <option key={task.id} value={task.id}>{task.title}</option>)}
              </select>
            </label>
            <label className="field">
              <span>关联技能</span>
              <select value={skillId} onChange={(event) => setSkillId(event.target.value)}>
                <option value="">不关联</option>
                {data?.skills.map((skill) => (
                  <option key={skill.id} value={skill.id}>{skill.title}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>关联联系人</span>
              <select value={contactId} onChange={(event) => setContactId(event.target.value)}>
                <option value="">不关联</option>
                {data?.contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name}{contact.organization ? ` · ${contact.organization}` : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="modal__footer">
            <Button variant="ghost" onClick={() => setStep("write")} icon={<ArrowLeft size={16} />}>
              返回原文
            </Button>
            <Button onClick={() => void submit()} disabled={!title.trim()} icon={<Check size={16} />}>
              确认写入
            </Button>
          </div>
        </>
      )}
    </ModalFrame>
  );
}

const modalCopy: Record<CreateKind, { title: string; subtitle: string }> = {
  goal: { title: "创建目标", subtitle: "说明你真正想改变的结果，而不只是要做的事。" },
  project: { title: "创建项目", subtitle: "项目应该有清晰结果和可结束的边界。" },
  task: { title: "创建任务", subtitle: "把下一步变得具体、可执行、可判断完成。" },
  skill: { title: "添加技能或兴趣", subtitle: "先确定发生场景与关联属性；经验只来自真实实践。" },
  contact: { title: "添加工作联系人", subtitle: "只记录推进关系所需的必要信息。" },
  transaction: { title: "记录一笔流水", subtitle: "余额将从真实流水自动汇总，不可直接修改。" },
  asset: { title: "新增资产余额", subtitle: "记录某个时点的真实资产，财富雷达会同步更新。" },
  profile: { title: "设置你的世界", subtitle: "这些信息只用于塑造你的个人操作系统。" }
};

function EntityModal({
  kind,
  taskDefaults
}: {
  kind: CreateKind;
  taskDefaults: { dueDate?: string; pinned?: boolean } | null;
}) {
  const { data, closeModal, mutate } = useApp();
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    const form = new FormData(event.currentTarget);
    let payload: Record<string, unknown> = Object.fromEntries(form.entries());

    if (kind === "goal" || kind === "project") {
      payload.priority = Number(payload.priority);
    }
    if (kind === "task") {
      payload = {
        ...payload,
        priority: Number(payload.priority),
        valueScore: Number(payload.valueScore),
        difficulty: Number(payload.difficulty),
        estimatedMinutes: payload.estimatedMinutes ? Number(payload.estimatedMinutes) : null,
        pinned: payload.pinned === "on",
        externalCommitment: payload.externalCommitment === "on"
      };
    }
    if (kind === "profile") {
      payload = {
        ...payload,
        energy: payload.energy ? Number(payload.energy) : null,
        focus: payload.focus ? Number(payload.focus) : null,
        mood: payload.mood ? Number(payload.mood) : null
      };
    }
    const endpoint: Record<CreateKind, string> = {
      goal: "goals",
      project: "projects",
      task: "tasks",
      skill: "skills",
      contact: "contacts",
      transaction: "transactions",
      asset: "assets",
      profile: "profile"
    };
    const saved = await mutate(
      () => api.create(endpoint[kind], payload),
      kind === "profile" ? "你的设置已保存" : "已创建并写入你的真实空间"
    );
    setSaving(false);
    if (saved) closeModal();
  };

  return (
    <ModalFrame {...modalCopy[kind]} onClose={closeModal}>
      <form onSubmit={(event) => void submit(event)}>
        <div className="modal__body form-grid">
          {kind === "goal" && <GoalFields />}
          {kind === "project" && <ProjectFields goals={data?.goals ?? []} />}
          {kind === "task" && (
            <TaskFields
              goals={data?.goals ?? []}
              projects={data?.projects ?? []}
              skills={data?.skills ?? []}
              contacts={data?.contacts ?? []}
              defaults={taskDefaults}
            />
          )}
          {kind === "skill" && <SkillFields />}
          {kind === "contact" && <ContactFields />}
          {kind === "transaction" && <TransactionFields />}
          {kind === "asset" && <AssetFields />}
          {kind === "profile" && <ProfileFields profile={data?.profile} />}
        </div>
        <div className="modal__footer">
          <Button type="button" variant="ghost" onClick={closeModal}>取消</Button>
          <Button type="submit" disabled={saving}>
            {saving ? "正在保存…" : kind === "profile" ? "保存设置" : "确认创建"}
          </Button>
        </div>
      </form>
    </ModalFrame>
  );
}

function GoalFields() {
  return (
    <>
      <label className="field">
        <span>目标名称 <b>*</b></span>
        <input name="title" required autoFocus placeholder="例如：建立稳定的内容获客能力" maxLength={120} />
      </label>
      <label className="field">
        <span>人生领域 <b>*</b></span>
        <select name="domain" defaultValue="事业">
          {["事业", "学习", "财富", "健康", "关系", "生活", "自定义"].map((item) => (
            <option key={item}>{item}</option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>为什么重要</span>
        <textarea name="whyImportant" rows={3} placeholder="这个目标会改变什么？" />
      </label>
      <label className="field">
        <span>成功指标</span>
        <textarea name="successMetric" rows={3} placeholder="什么事实出现时，代表目标达成？" />
      </label>
      <div className="form-row">
        <label className="field">
          <span>截止日期</span>
          <input type="date" name="deadline" />
        </label>
        <RatingField name="priority" label="优先级" defaultValue={4} />
      </div>
    </>
  );
}

function ProjectFields({ goals }: { goals: Array<{ id: string; title: string }> }) {
  return (
    <>
      <label className="field">
        <span>项目名称 <b>*</b></span>
        <input name="title" required autoFocus placeholder="一个有结束条件的项目" />
      </label>
      <label className="field">
        <span>关联目标</span>
        <select name="goalId" defaultValue="">
          <option value="">暂不关联</option>
          {goals.map((goal) => <option key={goal.id} value={goal.id}>{goal.title}</option>)}
        </select>
      </label>
      <label className="field">
        <span>预期成果</span>
        <textarea name="outcome" rows={3} placeholder="项目完成后，会出现什么可验证产出？" />
      </label>
      <label className="field">
        <span>下一步行动</span>
        <textarea name="nextAction" rows={2} placeholder="现在最自然的一步" />
      </label>
      <div className="form-row">
        <label className="field">
          <span>目标日期</span>
          <input type="date" name="deadline" />
        </label>
        <RatingField name="priority" label="优先级" defaultValue={3} />
      </div>
    </>
  );
}

function TaskFields({
  goals,
  projects,
  skills,
  contacts,
  defaults
}: {
  goals: Array<{ id: string; title: string }>;
  projects: Array<{ id: string; title: string }>;
  skills: Array<{ id: string; title: string }>;
  contacts: Array<{ id: string; name: string }>;
  defaults: { dueDate?: string; pinned?: boolean } | null;
}) {
  return (
    <>
      <label className="field">
        <span>任务名称 <b>*</b></span>
        <input name="title" required autoFocus placeholder="用动词开头，写清可交付结果" />
      </label>
      <label className="field">
        <span>完成标准</span>
        <textarea name="detail" rows={2} placeholder="做到什么程度算完成？" />
      </label>
      <div className="form-row">
        <label className="field">
          <span>关联项目</span>
          <select name="projectId" defaultValue="">
            <option value="">暂不关联</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.title}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>关联目标</span>
          <select name="goalId" defaultValue="">
            <option value="">暂不关联</option>
            {goals.map((goal) => <option key={goal.id} value={goal.id}>{goal.title}</option>)}
          </select>
        </label>
      </div>
      <div className="form-row">
        <label className="field">
          <span>截止日期</span>
          <input type="date" name="dueDate" defaultValue={defaults?.dueDate ?? ""} />
        </label>
        <label className="field">
          <span>预计时间（分钟）</span>
          <input type="number" name="estimatedMinutes" min={1} max={1440} placeholder="30" />
        </label>
      </div>
      <label className="field">
        <span>关联技能或兴趣</span>
        <select name="skillId" defaultValue="">
          <option value="">暂不关联技能证据</option>
          {skills.map((skill) => <option key={skill.id} value={skill.id}>{skill.title}</option>)}
        </select>
      </label>
      <label className="field">
        <span>关联联系人</span>
        <select name="contactId" defaultValue="">
          <option value="">暂不关联关系</option>
          {contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.name}</option>)}
        </select>
      </label>
      <div className="form-row form-row--three">
        <RatingField name="priority" label="优先级" defaultValue={3} />
        <RatingField name="valueScore" label="价值" defaultValue={3} />
        <RatingField name="difficulty" label="难度" defaultValue={2} />
      </div>
      <div className="checkboxes">
        <label><input type="checkbox" name="pinned" defaultChecked={defaults?.pinned ?? false} /> 加入今日置顶</label>
        <label><input type="checkbox" name="externalCommitment" /> 涉及对外承诺</label>
      </div>
    </>
  );
}

function SkillFields() {
  const [kind, setKind] = useState<"SKILL" | "INTEREST">("SKILL");
  const [sphere, setSphere] = useState<"WORK" | "LIFE">("WORK");
  const [growthDimensionId, setGrowthDimensionId] = useState<
    "attr-career" | "attr-experience" | "attr-physique"
  >("attr-career");

  return (
    <>
      <label className="field">
        <span>类型</span>
        <select
          name="kind"
          value={kind}
          onChange={(event) => {
            const next = event.target.value === "INTEREST" ? "INTEREST" : "SKILL";
            setKind(next);
            if (next === "INTEREST") {
              setSphere("LIFE");
              setGrowthDimensionId("attr-experience");
            }
          }}
        >
          <option value="SKILL">技能 · 可以反复练习的能力</option>
          <option value="INTEREST">兴趣 · 爱好与情操</option>
        </select>
      </label>
      <div className="form-row">
        <label className="field">
          <span>发生场景</span>
          <select
            name="sphere"
            value={sphere}
            onChange={(event) => {
              const next = event.target.value === "LIFE" ? "LIFE" : "WORK";
              setSphere(next);
              if (kind === "SKILL") {
                setGrowthDimensionId(next === "WORK" ? "attr-career" : "attr-experience");
              }
            }}
          >
            <option value="WORK">工作技能</option>
            <option value="LIFE">生活技能</option>
          </select>
        </label>
        <label className="field">
          <span>主要影响</span>
          <select
            name="growthDimensionId"
            value={growthDimensionId}
            onChange={(event) => setGrowthDimensionId(event.target.value as typeof growthDimensionId)}
          >
            <option value="attr-career">事业</option>
            <option value="attr-experience">经历</option>
            <option value="attr-physique">体魄</option>
          </select>
        </label>
      </div>
      <label className="field">
        <span>名称 <b>*</b></span>
        <input name="title" required autoFocus placeholder="例如：商务谈判、游泳、书法" />
      </label>
      <label className="field">
        <span>分类</span>
        <input name="category" defaultValue="综合" placeholder="例如：沟通、数字、运动、艺术" />
      </label>
      <label className="field">
        <span>技能说明</span>
        <textarea name="description" rows={4} placeholder="它能解决什么问题？怎样算掌握？" />
      </label>
      <div className="form-callout">
        <Sparkles size={18} />
        <p><strong>从真实使用开始。</strong>完成关联事项或确认运动事实后才会积累经验。财富仍只由资产现状决定。</p>
      </div>
    </>
  );
}

function ContactFields() {
  return (
    <>
      <label className="field">
        <span>联系人 <b>*</b></span>
        <input name="name" required autoFocus placeholder="姓名或称呼" />
      </label>
      <label className="field">
        <span>组织</span>
        <input name="organization" placeholder="公司、团队或关系背景" />
      </label>
      <label className="field">
        <span>当前上下文</span>
        <textarea name="context" rows={4} placeholder="已确认的事实、需求或承诺" />
      </label>
      <label className="field">
        <span>下次跟进</span>
        <input type="date" name="nextFollowup" />
      </label>
    </>
  );
}

function TransactionFields() {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <>
      <div className="form-row">
        <label className="field">
          <span>账户范围</span>
          <select name="accountType" defaultValue="PERSONAL">
            <option value="PERSONAL">个人</option>
            <option value="BUSINESS">业务</option>
          </select>
        </label>
        <label className="field">
          <span>流水类型</span>
          <select name="transactionType" defaultValue="EXPENSE">
            <option value="EXPENSE">支出</option>
            <option value="INCOME">收入</option>
          </select>
        </label>
      </div>
      <div className="form-row">
        <label className="field">
          <span>金额 <b>*</b></span>
          <input name="amount" inputMode="decimal" pattern="\d+(\.\d{1,2})?" required autoFocus placeholder="0.00" />
        </label>
        <label className="field">
          <span>币种</span>
          <select name="currency" defaultValue="CNY">
            {["CNY", "USD", "EUR", "HKD", "JPY"].map((currency) => (
              <option key={currency}>{currency}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span>大类 <b>*</b></span>
        <input name="category" required placeholder="例如：生活、工作、成长" />
      </label>
      <label className="field">
        <span>子类目</span>
        <input name="subcategory" placeholder="例如：餐饮、软件服务、项目回款" />
      </label>
      <label className="field">
        <span>备注</span>
        <textarea name="note" rows={3} placeholder="这笔流水的必要说明" />
      </label>
      <label className="field">
        <span>发生日期</span>
        <input type="date" name="occurredOn" defaultValue={today} required />
      </label>
    </>
  );
}

function AssetFields() {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <>
      <div className="form-row">
        <label className="field">
          <span>资产名称 <b>*</b></span>
          <input name="name" required autoFocus placeholder="例如：随身现金、工资卡、基金账户" />
        </label>
        <label className="field">
          <span>资产类型</span>
          <select name="assetType" defaultValue="CASH">
            <option value="CASH">现金</option>
            <option value="BANK">银行账户</option>
            <option value="INVESTMENT">投资</option>
            <option value="RECEIVABLE">应收款</option>
            <option value="FIXED">固定资产</option>
            <option value="OTHER">其他</option>
          </select>
        </label>
      </div>
      <div className="form-row">
        <label className="field">
          <span>当前余额 <b>*</b></span>
          <input name="amount" inputMode="decimal" pattern="\d+(\.\d{1,2})?" required placeholder="3000.00" />
        </label>
        <label className="field">
          <span>币种</span>
          <select name="currency" defaultValue="CNY">
            {["CNY", "USD", "EUR", "HKD", "JPY"].map((currency) => (
              <option key={currency}>{currency}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span>盘点日期</span>
        <input type="date" name="asOfDate" defaultValue={today} required />
      </label>
      <label className="field">
        <span>备注</span>
        <textarea name="note" rows={3} placeholder="这项资产的必要说明" />
      </label>
    </>
  );
}

function ProfileFields({ profile }: { profile: { display_name: string | null; timezone: string; current_version: string | null; energy: number | null; focus: number | null; mood: number | null } | null | undefined }) {
  return (
    <>
      <label className="field">
        <span>你希望被怎样称呼</span>
        <input name="displayName" autoFocus defaultValue={profile?.display_name ?? ""} placeholder="留空也可以" />
      </label>
      <label className="field">
        <span>时区</span>
        <select name="timezone" defaultValue={profile?.timezone ?? "Asia/Shanghai"}>
          <option value="Asia/Shanghai">中国标准时间 · UTC+8</option>
          <option value="Asia/Hong_Kong">香港时间 · UTC+8</option>
          <option value="Asia/Tokyo">东京时间 · UTC+9</option>
          <option value="Europe/London">伦敦时间</option>
          <option value="America/New_York">纽约时间</option>
          <option value="America/Los_Angeles">洛杉矶时间</option>
        </select>
      </label>
      <label className="field">
        <span>当前个人版本</span>
        <input name="currentVersion" defaultValue={profile?.current_version ?? ""} placeholder="例如：1.0 · 建立行动事实系统" />
      </label>
      <div className="form-callout form-callout--neutral">
        <Activity size={18} />
        <p><strong>快速状态打卡</strong>只描述此刻，不评价人格；可随时留空。</p>
      </div>
      <div className="form-row form-row--three">
        <RatingField name="energy" label="精力" defaultValue={profile?.energy ?? undefined} optional />
        <RatingField name="focus" label="专注" defaultValue={profile?.focus ?? undefined} optional />
        <RatingField name="mood" label="情绪" defaultValue={profile?.mood ?? undefined} optional />
      </div>
    </>
  );
}

function RatingField({
  name,
  label,
  defaultValue,
  optional = false
}: {
  name: string;
  label: string;
  defaultValue?: number;
  optional?: boolean;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select name={name} defaultValue={defaultValue?.toString() ?? ""}>
        {optional && <option value="">未记录</option>}
        {[1, 2, 3, 4, 5].map((value) => (
          <option value={value} key={value}>{value} / 5</option>
        ))}
      </select>
    </label>
  );
}

function SearchModal() {
  const { data, closeModal } = useApp();
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || !data) return [];
    return [
      ...data.tasks.map((item) => ({ id: item.id, title: item.title, kind: "任务", to: "/projects", icon: Goal })),
      ...data.projects.map((item) => ({ id: item.id, title: item.title, kind: "项目", to: "/projects", icon: Target })),
      ...data.events.map((item) => ({ id: item.id, title: item.title, kind: "记录", to: "/timeline", icon: Activity })),
      ...data.skills.map((item) => ({ id: item.id, title: item.title, kind: "技能", to: "/growth", icon: BookOpen })),
      ...data.contacts.map((item) => ({ id: item.id, title: item.name, kind: "联系人", to: "/work", icon: BriefcaseBusiness })),
      ...data.transactions.map((item) => ({ id: item.id, title: `${item.category} · ${item.note || item.currency}`, kind: "流水", to: "/finance", icon: BadgeDollarSign }))
    ].filter((item) => item.title.toLowerCase().includes(term)).slice(0, 12);
  }, [data, query]);

  return (
    <div className="modal-backdrop modal-backdrop--search" onMouseDown={closeModal}>
      <section className="search-modal" onMouseDown={(event) => event.stopPropagation()}>
        <div className="search-modal__input">
          <Search size={20} />
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索你的事实世界…"
          />
          <kbd>ESC</kbd>
        </div>
        <div className="search-modal__results">
          {!query.trim() ? (
            <div className="search-hint">
              <Search size={21} />
              <p>输入关键词，可搜索任务、项目、时间轴、技能、联系人和财务流水。</p>
            </div>
          ) : results.length === 0 ? (
            <div className="search-hint">
              <p>没有找到与“{query}”相关的真实记录。</p>
            </div>
          ) : (
            results.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={`${item.kind}-${item.id}`}
                  onClick={() => {
                    navigate(item.to);
                    closeModal();
                  }}
                >
                  <span><Icon size={17} /></span>
                  <div><strong>{item.title}</strong><small>{item.kind}</small></div>
                  <ChevronRight size={16} />
                </button>
              );
            })
          )}
        </div>
        <footer className="search-modal__footer">
          <span>搜索仅基于你已保存的真实记录</span>
          <small>{data ? `${data.tasks.length + data.projects.length + data.events.length} 条可检索事实` : ""}</small>
        </footer>
      </section>
    </div>
  );
}
